import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Tag, Plus, Copy, Check, X, Users, Calendar,
  RefreshCw, Loader2, Ban, Percent, DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

/**
 * Admin Promotions panel — creates Stripe-backed discount codes that are
 * auto-applied to the regular invite → checkout flow. Mirrors the
 * TrialCodesPanel pattern.
 */
export default function PromotionsPanel({ isDarkMode }) {
  const { api } = useAuth();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');
  const [form, setForm] = useState({
    label: '',
    code: '',
    discount_type: 'percent',
    discount_value: 20,
    duration: 'once',
    duration_in_months: 3,
    applies_to: ['monthly'],
    trial_days: 0,
    max_redemptions: '',
    expires_at: '',
    notes: '',
  });  const cardClass = isDarkMode
    ? 'bg-[#14161a] border border-white/10'
    : 'bg-white border border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const inputClass = isDarkMode
    ? 'bg-[#0a0b0f] border-white/10 text-white placeholder-gray-500'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400';

  const fetchPromos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/promotions');
      setPromos(res.data?.promotions || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load promotions');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchPromos(); }, [fetchPromos]);

  const setApplies = (key) => {
    setForm((f) => ({ ...f, applies_to: [key] }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) { toast.error('Label required'); return; }
    if (form.discount_type === 'percent' && (form.discount_value < 1 || form.discount_value > 100)) {
      toast.error('Percent must be 1–100'); return;
    }
    if (form.discount_type === 'amount' && form.discount_value < 1) {
      toast.error('Amount must be ≥ $1'); return;
    }
    if (form.duration === 'repeating' && !form.duration_in_months) {
      toast.error('Months required for "repeating"'); return;
    }
    setCreating(true);
    try {
      const payload = {
        label: form.label.trim(),
        code: form.code.trim() || undefined,
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        duration: form.duration,
        duration_in_months: form.duration === 'repeating' ? Number(form.duration_in_months) : undefined,
        applies_to: form.applies_to,
        trial_days: Number(form.trial_days) || 0,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : undefined,
        expires_at: form.expires_at
          ? new Date(form.expires_at).toISOString()
          : undefined,
        notes: form.notes.trim() || undefined,
      };
      const res = await api.post('/api/admin/promotions', payload);
      toast.success(`Promo ${res.data.code} created`);
      setForm({
        label: '', code: '', discount_type: 'percent', discount_value: 20,
        duration: 'once', duration_in_months: 3, applies_to: ['monthly'],
        trial_days: 0, max_redemptions: '', expires_at: '', notes: '',
      });      fetchPromos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create promotion');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id, code) => {
    if (!window.confirm(`Revoke ${code}? Existing subscribers keep their discount; no new redemptions will be accepted.`)) return;
    try {
      await api.post(`/api/admin/promotions/${id}/revoke`);
      toast.success(`${code} revoked`);
      fetchPromos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Revoke failed');
    }
  };

  const archive = async (id, code) => {
    if (!window.confirm(`Archive ${code} from the admin UI?`)) return;
    try {
      await api.delete(`/api/admin/promotions/${id}`);
      toast.success(`${code} archived`);
      fetchPromos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Archive failed');
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 1500);
  };

  const formatDiscount = (p) =>
    p.discount_type === 'percent'
      ? `${p.discount_value}% off`
      : `$${p.discount_value} off`;

  const formatDuration = (p) => {
    if (p.duration === 'once') return 'Once';
    if (p.duration === 'forever') return 'Forever';
    return `${p.duration_in_months} mo`;
  };

  const statusPill = (s) => {
    const map = {
      active:    { bg: 'bg-teal-400/15',   text: 'text-teal-300',   label: 'ACTIVE' },
      revoked:   { bg: 'bg-rose-400/15',   text: 'text-rose-300',   label: 'REVOKED' },
      exhausted: { bg: 'bg-amber-400/15',  text: 'text-amber-300',  label: 'EXHAUSTED' },
      archived:  { bg: 'bg-slate-400/15',  text: 'text-slate-300',  label: 'ARCHIVED' },
    };
    const v = map[s] || map.archived;
    return <span className={`text-[10px] font-bold tracking-widest px-2 py-0.5 rounded-full ${v.bg} ${v.text}`}>{v.label}</span>;
  };

  return (
    <div className={`${cardClass} rounded-xl p-6`} data-testid="admin-promotions-panel">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-400/10 flex items-center justify-center">
            <Tag className="text-teal-400" size={20} />
          </div>
          <div>
            <h3 className={`font-semibold ${textPrimary}`}>Promotion Codes</h3>
            <p className={`text-sm ${textSecondary}`}>Stripe-backed discounts — applied at checkout</p>
          </div>
        </div>
        <button
          onClick={fetchPromos}
          className={`p-2 rounded-lg hover:bg-white/5 ${textSecondary}`}
          title="Refresh"
          data-testid="promotions-refresh-btn"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Create form */}
      <form onSubmit={handleCreate} className="space-y-3 mb-5" data-testid="create-promotion-form">
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="Label (e.g. Founder Deal)"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className={`px-3 py-2 rounded-lg text-sm ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
            required
            data-testid="promo-label-input"
          />
          <input
            type="text"
            placeholder="Custom code (optional)"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            className={`px-3 py-2 rounded-lg text-sm font-mono uppercase ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
            data-testid="promo-code-input"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <select
            value={form.discount_type}
            onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
            className={`px-3 py-2 rounded-lg text-sm ${inputClass} border focus:outline-none`}
            data-testid="promo-type-select"
          >
            <option value="percent">% off</option>
            <option value="amount">$ off</option>
          </select>
          <div className="relative">
            <input
              type="number"
              min="1"
              max={form.discount_type === 'percent' ? 100 : 99999}
              step={form.discount_type === 'percent' ? 1 : 0.01}
              value={form.discount_value}
              onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
              className={`w-full pl-8 pr-3 py-2 rounded-lg text-sm ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
              data-testid="promo-value-input"
            />
            <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500">
              {form.discount_type === 'percent' ? <Percent size={12} /> : <DollarSign size={12} />}
            </div>
          </div>
          <select
            value={form.duration}
            onChange={(e) => setForm({ ...form, duration: e.target.value })}
            className={`px-3 py-2 rounded-lg text-sm ${inputClass} border focus:outline-none`}
            data-testid="promo-duration-select"
          >
            <option value="once">Once</option>
            <option value="repeating">Repeating…</option>
            <option value="forever">Forever</option>
          </select>
        </div>

        {form.duration === 'repeating' && (
          <div>
            <label className={`block text-xs ${textSecondary} mb-1`}>For how many months?</label>
            <input
              type="number"
              min="1"
              max="24"
              value={form.duration_in_months}
              onChange={(e) => setForm({ ...form, duration_in_months: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg text-sm ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
              data-testid="promo-duration-months-input"
            />
          </div>
        )}

        <div>
          <div className={`block text-xs ${textSecondary} mb-1.5`}>Applies to</div>
          <div className="flex gap-2">
            {[
              { key: 'monthly',  label: 'Monthly plans' },
              { key: 'lifetime', label: 'Lifetime' },
            ].map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setApplies(o.key)}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  form.applies_to[0] === o.key
                    ? 'bg-teal-400/15 border-teal-400/50 text-teal-200'
                    : 'bg-white/[0.02] border-white/10 text-slate-400 hover:text-slate-200'
                }`}
                data-testid={`promo-applies-${o.key}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <p className={`text-[10px] ${textSecondary} mt-1`}>Each code applies to one product type.</p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={`block text-xs ${textSecondary} mb-1`}>Trial days</label>
            <input
              type="number" min="0" max="90"
              value={form.trial_days}
              onChange={(e) => setForm({ ...form, trial_days: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg text-sm ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
              data-testid="promo-trial-days-input"
            />
          </div>
          <div>
            <label className={`block text-xs ${textSecondary} mb-1`}>Max uses</label>
            <input
              type="number" min="1"
              placeholder="unlimited"
              value={form.max_redemptions}
              onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg text-sm ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
              data-testid="promo-max-input"
            />
          </div>
          <div>
            <label className={`block text-xs ${textSecondary} mb-1`}>Expires</label>
            <input
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg text-sm ${inputClass} border focus:outline-none focus:ring-1 focus:ring-teal-400/50`}
              data-testid="promo-expires-input"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={creating}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-teal-400 text-[#0a0b0f] font-medium hover:bg-teal-500 transition-colors disabled:opacity-50"
          data-testid="create-promotion-submit-btn"
        >
          {creating ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
          Create Promotion
        </button>
      </form>

      {/* List */}
      <div className="space-y-2" data-testid="promotions-list">
        {loading && <div className={`text-center py-6 ${textSecondary}`}><Loader2 className="animate-spin inline" size={18} /></div>}
        {!loading && promos.length === 0 && (
          <div className={`text-center py-6 text-sm ${textSecondary}`}>No promotions yet.</div>
        )}
        {promos.map((p) => (
          <motion.div
            key={p.promotion_id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`flex items-center justify-between p-3 rounded-lg border ${isDarkMode ? 'bg-[#0a0b0f] border-white/5' : 'bg-gray-50 border-gray-200'}`}
            data-testid={`promotion-row-${p.promotion_id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <code className={`text-sm font-mono font-bold ${textPrimary}`}>{p.code}</code>
                <button
                  onClick={() => copy(p.code)}
                  className={`p-1 rounded hover:bg-white/5 ${textSecondary}`}
                  title="Copy code"
                  data-testid={`promotion-copy-${p.promotion_id}`}
                >
                  {copiedCode === p.code ? <Check size={12} className="text-teal-400" /> : <Copy size={12} />}
                </button>
                {statusPill(p.status)}
              </div>
              <div className={`text-xs ${textSecondary} truncate`}>
                <b className={textPrimary}>{formatDiscount(p)}</b> · {formatDuration(p)} · {(p.applies_to || []).join(' + ')}
                {p.trial_days > 0 && <> · +{p.trial_days}d trial</>}
                {p.label && <> · <i>{p.label}</i></>}
              </div>
              <div className={`text-xs ${textSecondary} mt-0.5 flex items-center gap-3`}>
                <span className="inline-flex items-center gap-1"><Users size={11} />{p.redemptions_used || 0}{p.max_redemptions ? `/${p.max_redemptions}` : ''}</span>
                {p.expires_at && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar size={11} />{new Date(p.expires_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 ml-3">
              {p.status === 'active' && (
                <button
                  onClick={() => revoke(p.promotion_id, p.code)}
                  className="p-1.5 rounded hover:bg-rose-400/10 text-rose-400"
                  title="Revoke"
                  data-testid={`promotion-revoke-${p.promotion_id}`}
                >
                  <Ban size={14} />
                </button>
              )}
              <button
                onClick={() => archive(p.promotion_id, p.code)}
                className="p-1.5 rounded hover:bg-white/5 text-slate-500"
                title="Archive"
                data-testid={`promotion-archive-${p.promotion_id}`}
              >
                <X size={14} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
